import {
  LayersOutlined,
  NavigateNext,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { TreeItem } from "@mui/lab";
import { Box, IconButton } from "@mui/material";
import React, { Dispatch, SetStateAction } from "react";

import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { Topics } from "shared/common/stores/sidebarStore";
import { BlockedLayer } from "shared/common/utils/icons/icons";
import { LayertreeLayers } from "./LayertreeLayers";

interface props {
  topic: Topics[];
  setTopic: Dispatch<SetStateAction<Topics[]>>;
  handleChange: (index: number[], showOnMap: boolean) => void;
  setChangeState: Dispatch<SetStateAction<boolean>>;
  changeState: boolean;
  changeExportFile: (id: string, visible: boolean) => void;
  checkShowFolder: (index: number) => void;
  item: Topics;
  indexInput: number[];
  layerMenuID: string;
  setLayerMenuID: Dispatch<SetStateAction<string>>;
  handleCloseEverythingOnRemoveLayer: () => void;
}

export const LayertreeGroups = ({
  topic,
  setTopic,
  handleChange,
  setChangeState,
  changeState,
  changeExportFile,
  checkShowFolder,
  item,
  indexInput,
  layerMenuID,
  setLayerMenuID,
  handleCloseEverythingOnRemoveLayer,
}: props) => {
  const apm = defaultStore((state) => state.apm);
  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);

  //function to make group of layers visible or not visible
  const handleCallChanges = (showOnMap: boolean, index: number[]) => {
    if (viewer !== undefined) {
      topic[index[0]].showOnMap = !showOnMap;
      setTopic([...topic]);
      let addToIndex = 0;

      if (item.children) {
        item.children.forEach((element: Topics) => {
          const index = [...indexInput, addToIndex];
          addToIndex++;
          handleChange(index, !showOnMap);

          if (element.layer) {
            if (showOnMap) {
              element.layer.setVisible(false);
              topic[index[0]].showOnMap = false;
              setChangeState(!changeState);
              changeExportFile(element.layer.id, false);
            } else {
              element.layer.setVisible(true);
              topic[index[0]].showOnMap = true;
              setChangeState(!changeState);
              changeExportFile(element.layer.id, true);
            }
          }
        });
      }
    }
  };

  return (
    <Box
      position={"relative"}
      width="100%"
      key={item.key}
      display={"flex"}
      flexDirection="row"
      alignItems={"top"}
      marginBottom={0.5}
    >
      <Box
        position={"relative"}
        display={"flex"}
        flexDirection="row"
        alignItems={"top"}
        width="100%"
        gap={0.5}
        sx={{
          color: darkTheme ? "white" : "darkblue.main",
          "@media (hover: hover)": {
            ":hover": { color: "secondary.main", cursor: "pointer" },
          },
        }}
      >
        {indexInput.length > 1 && (
          <Box
            width={"20px"}
            height="32px"
            display={"flex"}
            alignItems="center"
            marginLeft={"-12.5px"}
            sx={{
              borderLeft: darkTheme ? "1px white solid" : "1px #05395277 solid",
            }}
          >
            <Box
              sx={{
                width: "16px",
                height: "0px",
                borderTop: darkTheme
                  ? "1px white solid"
                  : "1px #05395277 solid",
              }}
            ></Box>
            <NavigateNext fontSize="small" />
          </Box>
        )}
        {item.addedFromUser ? (
          <LayersOutlined sx={{ paddingTop: "2px" }} fontSize="small" />
        ) : (
          <BlockedLayer />
        )}
        <TreeItem
          sx={{
            backgroundColor: darkTheme ? "darkblue.main" : "white",
            "@media (hover: hover)": {
              ":hover": { color: "secondary.main" },
            },
          }}
          classes={{
            root: "infra3d-rootTreeItem",
            iconContainer: "infra3d-iconTreeItem",
            label: "infra3d-labelTreeItemWithIcon",
            content: "infra3d-contentTreeItem",
            group: "infra3d-groupTreeItem",
          }}
          nodeId={item.key ? item.key : ""}
          label={item.name}
        >
          {item.children &&
            item.children.map((children: Topics, index: number) => (
              <React.Fragment key={children.key}>
                {children.folder && (
                  <LayertreeGroups
                    topic={topic}
                    setTopic={setTopic}
                    handleChange={handleChange}
                    setChangeState={setChangeState}
                    changeState={changeState}
                    changeExportFile={changeExportFile}
                    checkShowFolder={checkShowFolder}
                    item={children}
                    indexInput={[index]}
                    layerMenuID={layerMenuID}
                    setLayerMenuID={setLayerMenuID}
                    handleCloseEverythingOnRemoveLayer={
                      handleCloseEverythingOnRemoveLayer
                    }
                  />
                )}
                {!children.folder && (
                  <LayertreeLayers
                    topic={topic}
                    handleChange={handleChange}
                    setChangeState={setChangeState}
                    changeState={changeState}
                    changeExportFile={changeExportFile}
                    checkShowFolder={checkShowFolder}
                    children={children}
                    index={[...indexInput, index]}
                    layerMenuID={layerMenuID}
                    setLayerMenuID={setLayerMenuID}
                    length={item.children?.length || 0}
                    handleCloseEverythingOnRemoveLayer={
                      handleCloseEverythingOnRemoveLayer
                    }
                  />
                )}
              </React.Fragment>
            ))}
        </TreeItem>
      </Box>
      <IconButton
        onClick={() =>
          monitor(
            apm,
            "change-visibility-layergroup | Layertree",
            () => handleCallChanges(item.showOnMap, indexInput),
            { layername: item.name, visible: !item.showOnMap }
          )
        }
        disableRipple={true}
        sx={{
          padding: "0px",
          position: "absolute",
          right: "0px",
          color: darkTheme ? "white" : "darkblue.main",
          "@media (hover: hover)": {
            ":hover": { color: "secondary.main" },
          },
        }}
      >
        {item.showOnMap ? (
          <VisibilityOutlined fontSize="small" />
        ) : (
          <VisibilityOffOutlined fontSize="small" />
        )}
      </IconButton>
    </Box>
  );
};
